import React, { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import ActionsModal from 'components/ActionsModal/ActionsModal'
import styles from '../../containers/Groups/Groups.module.scss'
import { selectDisplayedGroup, updateDisplayedGroup, closeDisplayedGroup } from 'store/organizations'
import messages from './messages'
import { selectAvailableZones, selectAllGroups } from 'store/global'
import DropdownInput from 'components/DropdownInput/DropdownInput'
import OrganizationZones from 'components/OrganizationZones/OrganizationZones'

const allowedModes = ['create', 'edit', 'delete', 'view'] as const
const MODE_CREATE = 'create'
const MODE_EDIT = 'edit'
const MODE_VIEW = 'view'

type Mode = typeof allowedModes[number]

interface Group {
  id: string
  path: string
  subGroups?: Group[]
}

interface Zone {
  id: string
  name: string
}

interface GroupOption {
  value: string
  label: string
  path?: string
}

interface GroupModalProps {
  mode: Mode
  onSubmit: (group: any) => void | Promise<void>
}

export default function GroupModal({ mode, onSubmit }: GroupModalProps) {
  const dispatch = useDispatch()

  const displayedGroup = useSelector(selectDisplayedGroup)

  const availableZones = useSelector(selectAvailableZones)
  const allGroups = useSelector(selectAllGroups)

  const isView = mode === MODE_VIEW

  const onFieldChange = (key: string, value: any) => dispatch(updateDisplayedGroup({ key, value }))

  const onCloseModal = () => dispatch(closeDisplayedGroup())

  const onZonesChange = (key: string, nextZonesSelections: GroupOption[]) => {
    const nextZonesIds = nextZonesSelections.map(({ value }) => value)
    onFieldChange(key, nextZonesIds)
  }

  const onParentGroupChange = (key: string, nextParentGroupSelections: GroupOption[]) => {
    const parentGroupId = nextParentGroupSelections.length > 0 ?
      nextParentGroupSelections[0].value : null
    onFieldChange('parentGroupId', parentGroupId)
    dispatch(updateDisplayedGroup({ key: 'parentId', value: parentGroupId }))
  }

  const zoneSelectedOptions = useMemo(() => {
    if (!displayedGroup || !displayedGroup.zoneIds) {
      return []
    }

    return displayedGroup.zoneIds
      .map((zoneId: string) => {
        const zone = availableZones.find((zone: Zone) => zone.id === zoneId)
        return zone ? { value: zoneId, label: zone.name } : null
      })
      .filter((option): option is GroupOption => option !== null)
  }, [displayedGroup, availableZones])

  const getParentGroupFromPath = (groupPath: string): string | null => {
    if (!groupPath) return null

    const parts = groupPath.split('/').filter(part => part !== '')
    if (parts.length <= 1) {
      return '/'
    } else {
      return '/' + parts.slice(0, -1).join('/')
    }
  }

  const parentGroupPath = useMemo(() => {
    if (!displayedGroup || !displayedGroup.path) return null
    return getParentGroupFromPath(displayedGroup.path)
  }, [displayedGroup])

  const parentGroupSelectedOptions = useMemo(() => {
    if (displayedGroup?.parentId) {
      const findGroupById = (groups: Group[], targetId: string): Group | null => {
        for (const group of groups || []) {
          if (group.id === targetId) {
            return group
          }
          if (group.subGroups && group.subGroups.length > 0) {
            const found = findGroupById(group.subGroups, targetId)
            if (found) return found
          }
        }
        return null
      }

      const parentGroup = findGroupById(allGroups, displayedGroup.parentId)
      return parentGroup ? [{ value: parentGroup.id, label: parentGroup.path }] : []
    }

    if (!parentGroupPath || parentGroupPath === '/') {
      return []
    }

    const findGroupByPath = (groups: Group[], targetPath: string): Group | null => {
      for (const group of groups || []) {
        if (group.path === targetPath) {
          return group
        }
        if (group.subGroups && group.subGroups.length > 0) {
          const found = findGroupByPath(group.subGroups, targetPath)
          if (found) return found
        }
      }
      return null
    }

    const parentGroup = findGroupByPath(allGroups, parentGroupPath)
    return parentGroup ? [{ value: parentGroup.id, label: parentGroup.path }] : []
  }, [displayedGroup?.parentId, parentGroupPath, allGroups])

  const zonesOptions = availableZones.map(({ id: value, name: label }: Zone) => ({
    value,
    label,
  }))

  const getAllGroupsRecursively = (groups: Group[]): GroupOption[] => {
    let allGroups: GroupOption[] = []
    groups.forEach(group => {
      allGroups.push({
        value: group.id,
        label: group.path,
        path: group.path
      })
      if (group.subGroups && group.subGroups.length > 0) {
        allGroups = allGroups.concat(getAllGroupsRecursively(group.subGroups))
      }
    })
    return allGroups
  }

  const findGroup = (groups: Group[], targetId: string): Group | null => {
    for (const group of groups || []) {
      if (group.id === targetId) {
        return group
      }
      if (group.subGroups && group.subGroups.length > 0) {
        const found = findGroup(group.subGroups, targetId)
        if (found) return found
      }
    }
    return null
  }

  const collectDescendantIds = (group: Group): string[] => {
    const descendantIds: string[] = []

    const collectRecursively = (subGroups?: Group[]) => {
      for (const subGroup of subGroups || []) {
        descendantIds.push(subGroup.id)
        if (subGroup.subGroups && subGroup.subGroups.length > 0) {
          collectRecursively(subGroup.subGroups)
        }
      }
    }

    collectRecursively(group.subGroups)
    return descendantIds
  }

  const getAllDescendantIds = (groups: Group[], parentId: string): string[] => {
    const parentGroup = findGroup(groups, parentId)
    if (!parentGroup) {
      return []
    }
    return collectDescendantIds(parentGroup)
  }

  const allGroupsOptions = useMemo(() => {
    if (!allGroups) return []
    const allGroupsList = getAllGroupsRecursively(allGroups)
    if (mode === MODE_EDIT && displayedGroup?.id) {
      const excludedIds = new Set([displayedGroup.id, ...getAllDescendantIds(allGroups, displayedGroup.id)])
      return allGroupsList.filter(group => !excludedIds.has(group.value))
    }
    return allGroupsList
  }, [allGroups, mode, displayedGroup?.id])

  const fields = {
    name: {
      key: 'name',
      isRequired: true,
      validator: (value: string) => value.length > 0,
      label: <FormattedMessage {...messages.labels.name} />,
    },
    parentGroup: {
      key: 'parentGroup',
      label: <FormattedMessage {...messages.labels.parentGroup} />,
      renderInput: () =>
        isView ? (
          <div className={styles.parentGroupView}>
            <strong>Parent Group Path:</strong> {parentGroupPath || 'None'}
          </div>
        ) : (mode === MODE_CREATE || mode === MODE_EDIT) ? (
          <DropdownInput
            options={allGroupsOptions}
            isMultiSelect={false}
            selections={parentGroupSelectedOptions}
            onChange={(nextParentGroupSelections) => onParentGroupChange('parentGroupId', nextParentGroupSelections)}
            hasSelectAll={false}
            disableSearch={false}
            dropdownHeader="Select parent group..."
          />
        ) : null,
    },
    zones: {
      key: 'zones',
      label: <FormattedMessage {...messages.labels.zones} />,
      disabled: isView,
      renderInput: () =>
        isView ? (
          <OrganizationZones isColumn organization={displayedGroup} />
        ) : (
          <DropdownInput
            options={zonesOptions}
            selections={zoneSelectedOptions}
            onChange={(nextZonesSelections) => onZonesChange('zoneIds', nextZonesSelections)}
          />
        ),
    },
  }

  return (
    <ActionsModal
      type='group'
      mode={mode}
      item={displayedGroup}
      primaryKey='name'
      fields={fields}
      onFieldChange={onFieldChange}
      onClose={onCloseModal}
      onSubmit={() => onSubmit(displayedGroup)}
    />
  )
}

